/**
 * Build the PostHog `organization` group payload from an organization document.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A MODULE, AND WHY IT NEVER THROWS
 *
 * On 2026-08-13 a paying customer (Plutus) was locked out of their business entirely — iOS AND the
 * web fallback, so they could not even reach in-app support and had to email us. 37 fatal events,
 * `handled: no`, one line:
 *
 *     currency: org.currency.id        // AppStack.js, inside a Firestore onSnapshot callback
 *
 * Their `organizations/{id}` document had been deleted and re-created by a subscription webhook's
 * `{merge: true}` write, leaving a six-field stub with no `name` and no `currency`. The snapshot
 * callback has no try/catch, so an undefined dereference in TELEMETRY became an uncaught throw at
 * startup, and React Native killed the app. The line had been correct for 14 months; nothing about
 * it changed. What changed was the data.
 *
 * The lesson this module encodes: **analytics is never load-bearing.** A malformed organization
 * document is a data problem to report, not a reason the cashier cannot open the till. So every
 * field here is optional, the function cannot throw, and the caller is expected to wrap it anyway.
 *
 * The same shape was already documented one day earlier, in the staging seed work (commit a81ed7fc):
 * a bare `currency: 'KZT'` string made `CURRENCIES.find(e => e.id === undefined)` return undefined
 * and the admin billing update die on `.exchangeRate`. It was called out there as an app-level
 * defect and left unfixed. This is that defect, in production, on a real customer.
 */

/**
 * @param {Record<string, any> | null | undefined} org an organization document, possibly malformed
 * @returns {Record<string, any>} a payload safe to hand to any analytics SDK
 */
export function buildOrganizationAnalyticsGroup(org) {
  if (!org || typeof org !== 'object') return {}

  // Every read below tolerates absence. `toDate` is optional-called because a Firestore Timestamp
  // becomes a plain value once it has been through a cache or a JSON round-trip.
  const limitUsd = org.subscriptionPlanInfo?.limits?.sales?.usd
  const salesUsd = org.currentMonthSalesUSD
  const liftBlockTill = org.liftBlockTill?.toDate?.()

  return {
    name: org.name,
    createdDate: org.createdDate?.toDate?.(),
    // The line that took the customer down. `currency` is an OBJECT in this codebase
    // (apps/atlas/utils/currencies.js), and a stub document has none at all.
    currency: org.currency?.id,
    pricingModel: org.pricingModel,
    businessType: org.businessType,
    trafficSource: org.trafficSource,
    setupOption: org.setupOption,
    subscriptionStatus: org.subscriptionStatus,
    subscriptionPlan: org.subscriptionPlan,
    subscriptionSource: org.subscriptionSource,
    subscriptionLastUpdated: org.subscriptionLastUpdated?.toDate?.(),
    currentMonthSalesUSD: salesUsd,
    overBillingLimits: Boolean(limitUsd && salesUsd > limitUsd),
    isBlocked: Boolean(
      limitUsd && salesUsd > limitUsd * 1.2 && (org.liftBlockTill == null || liftBlockTill < new Date()),
    ),
    ...(org.funnelAnswers ?? {}),
  }
}

/**
 * Resolve an organization's currency to something with an exchange rate, or null.
 *
 * The billing branch used to do `CURRENCIES.find(e => e.id === org.currency.id).exchangeRate`, which
 * throws twice over on a stub document: once on `org.currency.id`, and again on `.exchangeRate` of
 * the `undefined` that `find` returns for an unknown id. Inside a `.then()` that is an unhandled
 * rejection, which is why it produced no console message and no obvious diagnostic.
 *
 * @param {Record<string, any> | null | undefined} org
 * @param {Array<{id: string, exchangeRate: number}>} currencies
 * @returns {{id: string, exchangeRate: number} | null} null when it cannot be resolved
 */
export function resolveOrganizationCurrency(org, currencies) {
  const id = org?.currency?.id
  if (!id || !Array.isArray(currencies)) return null
  const match = currencies.find((element) => element?.id === id)
  return match && typeof match.exchangeRate === 'number' && match.exchangeRate > 0 ? match : null
}
